import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download, FileArchive, FileSpreadsheet, LayoutGrid, Pencil, Plus, RefreshCw, Settings2, Trash2 } from 'lucide-react';
import { api } from '../api.js';
import {
  Button, Field, Header, Modal, MoneyField, Pagination, Select, Status, Table, Tabs,
  can, dayLabel, fromLocalInput, localInput, money, op, operationalDate, queryString, statusNames, timeOnly, today,
  useAsk, useOpsContext, useQuery,
} from './ui.jsx';

import { BOOKABLE_TYPES as bookableTypes, RESOURCE_TYPES as resourceTypes, TYPE_INFO } from './resourceTypes.js';

const typeClass = Object.fromEntries(Object.entries(TYPE_INFO).map(([key, info]) => [key, info.cls]));
const typeName = Object.fromEntries(Object.entries(TYPE_INFO).map(([key, info]) => [key, info.name]));
// Booking chỉ còn hai trạng thái với nhân viên: Chưa đến / Đã đến (dữ liệu cũ có thể còn trạng thái khác)
const arrived = (row) => row.status === 'checked_in' || row.status === 'completed';
const waiting = (row) => row.status === 'pending' || row.status === 'confirmed';

/** Ảnh loại máy / dịch vụ (đồng bộ với Station); loại không có ảnh dùng biểu tượng tự vẽ. */
function MachineTypeIcon({ type }) {
  if (TYPE_INFO[type]?.image) return <img className="machine-type-image" src={TYPE_INFO[type].image} alt="" loading="lazy" />;
  if (type === 'Nintendo') return <svg viewBox="0 0 48 48" aria-hidden="true">
    <rect className="icon-fill" x="6" y="4" width="36" height="40" rx="11" />
    <rect x="8" y="6" width="32" height="36" rx="9" />
    <path d="M24 7v34" />
    <circle className="icon-solid" cx="16" cy="17" r="3" />
    <circle className="icon-solid" cx="32" cy="31" r="3" />
    <circle cx="32" cy="17" r="1.5" />
    <circle cx="16" cy="31" r="1.5" />
  </svg>;
  if (type === 'Private Room') return <svg viewBox="0 0 48 48" aria-hidden="true">
    <path className="icon-fill" d="m7 14 10 8L24 8l7 14 10-8-5 24H12L7 14Z" />
    <path d="m9 16 8 7 7-13 7 13 8-7-4 20H13L9 16Z" />
    <path d="M14 36h20M17 41h14" />
    <path className="icon-solid" d="m24 18 2.5 4-2.5 4-2.5-4 2.5-4Z" />
    <path d="M7 9h4M9 7v4M37 8h4M39 6v4" />
  </svg>;
  if (type === 'PS5') return <svg viewBox="0 0 48 48" aria-hidden="true">
    <path className="icon-fill" d="M14 16h20c6 0 9 5 10 12l1 7c1 6-5 10-10 5l-5-5H18l-5 5C8 45 2 41 3 35l1-7c1-7 4-12 10-12Z" />
    <path d="M14 18h20c5 0 7 4 8 10l1 6c.6 4-3.5 7-6.5 4l-5-5h-15l-5 5c-3 3-7.1 0-6.5-4l1-6c1-6 3-10 8-10Z" />
    <path d="M15 24v8M11 28h8" />
    <circle className="icon-solid" cx="33" cy="25" r="1.6" />
    <circle className="icon-solid" cx="37" cy="29" r="1.6" />
    <path d="M22 21h4" />
  </svg>;
  return <svg viewBox="0 0 48 48" aria-hidden="true">
    <rect className="icon-fill" x="5" y="8" width="38" height="29" rx="7" />
    <rect x="7" y="10" width="34" height="25" rx="5" />
    <path d="M18 41h12M24 35v6" />
  </svg>;
}

const blankForm = (storeId, day) => ({ store_id: storeId || '', resource_type: 'PS5', customer_name: '', phone: '', date: day, time: '', party_size: 2, deposit: '', notes: '' });

export function BookingPage() {
  const { auth, stores, fail, success } = useOpsContext();
  const [tab, setTab] = useState('board');
  const [storeId, setStoreId] = useState('');
  const [config, setConfig] = useState(false);
  const [reorderStore, setReorderStore] = useState(null);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [ask, dialog] = useAsk();
  const canData = can(auth, 'bookings.export');
  // Ngày vận hành đổi lúc 03:00 sáng: sau mốc này bảng tự bỏ booking của ngày cũ
  const [day, setDay] = useState(operationalDate());

  const resources = useQuery(`${op}/booking-resources${queryString({ store_id: storeId })}`);
  const areas = useQuery(`${op}/booking-areas${queryString({ store_id: storeId })}`);
  const board = useQuery(tab === 'board' ? `${op}/booking-board${queryString({ store_id: storeId })}` : null);
  // Mọi booking từ hôm nay trở đi, giờ sớm trước
  const upcoming = useQuery(tab !== 'data' ? `${op}/bookings${queryString({ store_id: storeId, from: day, to: '2099-12-31', order: 'asc', page_size: 200 })}` : null);
  const reloadBoard = board.reload;
  useEffect(() => {
    const timer = setInterval(() => { setDay(operationalDate()); if (tab === 'board') reloadBoard(); }, 60000);
    return () => clearInterval(timer);
  }, [tab, reloadBoard]);
  const reloadAll = () => { board.reload(); upcoming.reload(); };

  // Bấm thẻ máy: bật/tắt tay Trống ⇄ Đang chơi (không lấy từ Station)
  const togglePlaying = async (machine) => {
    try {
      await api.patch(`${op}/booking-resources/${machine.id}/playing`, { playing: !machine.manual_playing });
      success(`${machine.name}: ${machine.manual_playing ? 'Trống' : 'Đang chơi'}`);
      reloadAll();
    } catch (error) { fail(error); }
  };
  const setArrived = async (row, value) => {
    if ((value === 'checked_in') === arrived(row)) return;
    try {
      await api.patch(`${op}/bookings/${row.id}/status`, { status: value });
      reloadAll();
    } catch (error) { fail(error); }
  };
  // Xoá: chỉ ẩn khỏi màn hình, dữ liệu vẫn lưu trong database
  const hideBooking = async (row) => {
    if (!await ask({ title: `Xoá booking của ${row.customer_name}?`, message: 'Booking chỉ bị ẩn khỏi màn hình; dữ liệu vẫn được lưu trong hệ thống.', danger: true, confirmLabel: 'Xoá' })) return;
    try {
      await api.post(`${op}/bookings/${row.id}/hide`, {});
      success('Đã xoá booking khỏi màn hình');
      reloadAll();
    } catch (error) { fail(error); }
  };

  const baseColumns = [
    { key: 'customer_name', label: 'Tên khách' }, { key: 'phone_normalized', label: 'Số điện thoại' },
    { key: 'starts_at', label: 'Ngày đặt', render: (v) => dayLabel(localInput(v)) },
    { key: 'ends_at', label: 'Giờ đặt', render: (v, row) => timeOnly(row.starts_at) },
    { key: 'store_name', label: 'Cơ sở' }, { key: 'resource_type', label: 'Loại máy', render: (v) => typeName[v] || v || '—' },
  ];
  const depositColumn = { key: 'deposit', label: 'Đặt cọc', align: 'right', render: (v) => <span className={Number(v) ? 'pos' : ''}>{money(v)}</span> };
  const noteColumn = { key: 'notes', label: 'NV nhận - Ghi chú', render: (v) => v || '' };
  const badge = (row) => (arrived(row) ? <span className="status-pill active">Đã đến</span> : waiting(row) ? <span className="status-pill overdue">Chưa đến</span> : <Status value={row.status} />);
  const statusSelect = (row) => (!arrived(row) && !waiting(row)) || !can(auth, 'bookings.update') ? badge(row)
    : <select className={`status-select ${arrived(row) ? 'arrived' : 'waiting'}`} value={arrived(row) ? 'checked_in' : 'pending'} onChange={(event) => setArrived(row, event.target.value)} aria-label={`Trạng thái của ${row.customer_name}`}>
      <option value="pending">Chưa đến</option><option value="checked_in">Đã đến</option>
    </select>;
  const listColumns = [...baseColumns, { key: 'party_size', label: 'Số người', align: 'right' }, depositColumn, noteColumn, { key: 'status', label: 'Trạng thái', render: (v, row) => statusSelect(row) }];
  const miniColumns = [...baseColumns, depositColumn, noteColumn, { key: 'status', label: 'Trạng thái', render: (v, row) => badge(row) }];

  const boardByStore = useMemo(() => {
    const groups = new Map();
    for (const item of board.rows || []) {
      if (!groups.has(item.store_id)) groups.set(item.store_id, { store_id: item.store_id, store_name: item.store_name, machines: [] });
      groups.get(item.store_id).machines.push(item);
    }
    return [...groups.values()];
  }, [board.rows]);

  const tabs = [{ id: 'board', label: 'Máy trống' }, { id: 'list', label: 'Thông tin khách đặt' }, ...(canData ? [{ id: 'data', label: 'Dữ liệu khách hàng' }] : [])];

  const canManage = Boolean(can(auth, 'branches.manage'));
  const canReorder = Boolean(board.data?.can_reorder || canManage);

  const editMachine = async (machine) => {
    const storeAreas = (areas.rows || []).filter((item) => Number(item.store_id) === Number(machine.store_id));
    const values = await ask({
      title: `Chỉnh sửa máy - ${machine.name}`,
      fields: [
        { key: 'name', label: 'Tên máy', required: true, defaultValue: machine.name },
        {
          key: 'resource_type',
          label: 'Loại máy',
          required: true,
          defaultValue: machine.resource_type,
          placeholder: null,
          options: resourceTypes.map((id) => ({ id, name: typeName[id] || id })),
        },
        {
          key: 'status',
          label: 'Trạng thái',
          required: true,
          defaultValue: machine.status,
          placeholder: null,
          options: [
            { id: 'active', name: 'Hoạt động' },
            { id: 'maintenance', name: 'Bảo trì' },
            { id: 'inactive', name: 'Ngừng hoạt động (Ẩn khỏi sơ đồ)' },
          ],
        },
        ...(storeAreas.length > 0 ? [{
          key: 'area_id',
          label: 'Khu vực',
          defaultValue: machine.area_id ? String(machine.area_id) : '',
          placeholder: 'Không chia khu vực',
          options: [{ id: '', name: 'Không chia khu vực' }, ...storeAreas.map((a) => ({ id: String(a.id), name: a.name }))],
        }] : []),
      ],
      confirmLabel: 'Lưu thay đổi',
    });
    if (values) {
      try {
        await api.put(`${op}/booking-resources/${machine.id}`, {
          name: values.name.trim(),
          resource_type: values.resource_type,
          status: values.status,
          area_id: values.area_id ? Number(values.area_id) : null,
        });
        success(`Đã cập nhật máy "${values.name}"`);
        reloadAll();
        resources.reload();
      } catch (error) {
        fail(error);
      }
    }
  };

  const deleteMachine = async (machine) => {
    const ok = await ask({
      title: `Xóa máy "${machine.name}"?`,
      message: `Bạn có chắc chắn muốn xóa "${machine.name}" (${typeName[machine.resource_type] || machine.resource_type})?`,
      danger: true,
      confirmLabel: 'Xóa máy',
    });
    if (!ok) return;
    try {
      await api.delete(`${op}/booking-resources/${machine.id}`);
      success(`Đã xóa máy "${machine.name}"`);
      reloadAll();
      resources.reload();
    } catch (err) {
      if (err?.code === 'RESOURCE_IN_USE' || err?.message?.includes('RESOURCE_IN_USE') || err?.message?.includes('lịch sử booking')) {
        const deactivate = await ask({
          title: 'Máy đã có lịch sử booking',
          message: `Máy "${machine.name}" đã từng có khách đặt trước đây nên không thể xóa hoàn toàn khỏi cơ sở dữ liệu. Bạn có muốn chuyển máy sang trạng thái "Ngừng hoạt động" để ẩn khỏi sơ đồ máy không?`,
          confirmLabel: 'Ngừng hoạt động',
        });
        if (deactivate) {
          try {
            await api.put(`${op}/booking-resources/${machine.id}`, {
              name: machine.name,
              resource_type: machine.resource_type,
              status: 'inactive',
              area_id: machine.area_id ? Number(machine.area_id) : null,
            });
            success(`Đã chuyển máy "${machine.name}" sang ngừng hoạt động`);
            reloadAll();
            resources.reload();
          } catch (deactivateErr) {
            fail(deactivateErr);
          }
        }
      } else {
        fail(err);
      }
    }
  };

  return <main className="content-page">
    <Header eyebrow="BOOKING" title="Quản lý booking" description="Ghi nhận khách đặt lịch theo loại máy; bảng máy bật/tắt tay Trống – Đang chơi.">
      {stores.length > 1 && <Select label="Cơ sở" value={storeId} onChange={setStoreId} options={stores} placeholder="Tất cả cơ sở" />}
      <Button onClick={() => setConfig(true)}><Settings2 size={16} /> Quản lý máy & khu vực</Button>
    </Header>
    <Tabs value={tab} onChange={setTab} tabs={tabs} />

    {tab === 'board' && <>
      {board.error && <div className="ops-empty error">{board.error} <Button onClick={board.reload}><RefreshCw size={15} /> Thử lại</Button></div>}
      {!board.error && board.rows?.length === 0 && <div className="card ops-empty">Chưa khai báo máy nào. Bấm "Quản lý máy & khu vực" để thêm máy cho cơ sở.</div>}
      {boardByStore.map((group) => {
        const rows = (upcoming.rows || []).filter((row) => Number(row.store_id) === Number(group.store_id));
        const currentOrder = board.data?.type_orders?.[group.store_id] || resourceTypes;
        return <section className="card panel facility" key={group.store_id}>
          <div className="panel-title">
            <h2>{group.store_name}</h2>
            <div className="board-legend">
              <Status value="available" /><Status value="playing" /><Status value="maintenance" />
              {canManage && (
                <Button
                  tone={editingStoreId === group.store_id ? 'primary' : 'default'}
                  onClick={() => setEditingStoreId(editingStoreId === group.store_id ? null : group.store_id)}
                >
                  <Pencil size={15} /> {editingStoreId === group.store_id ? 'Xong' : 'Chỉnh sửa'}
                </Button>
              )}
              {canReorder && (
                <Button onClick={() => setReorderStore({ store_id: group.store_id, store_name: group.store_name, current_order: currentOrder, machines: group.machines })}>
                  <LayoutGrid size={15} /> Sắp xếp máy
                </Button>
              )}
            </div>
          </div>
          {/* Một hàng ngang giống cũ (nhiều hơn 12 máy thì chia đều 2 hàng) */}
          <div className="machine-row" style={{ '--machine-cols': group.machines.length > 12 ? Math.ceil(group.machines.length / 2) : Math.max(group.machines.length, 1) }}>
            {group.machines.map((machine) => {
              const isEditing = editingStoreId === group.store_id;
              const clickable = !isEditing && can(auth, 'bookings.update') && machine.status === 'active';
              const playing = machine.board_status === 'playing';
              const state = machine.status === 'maintenance' ? 'maintenance' : playing ? 'playing' : 'available';
              return <div role="button" tabIndex={0} key={machine.id}
                className={`machine-card ${typeClass[machine.resource_type] || 'other'} ${state} ${isEditing ? 'editing-mode' : !clickable ? 'disabled' : ''}`}
                aria-label={`${machine.name}, ${typeName[machine.resource_type] || machine.resource_type}, ${statusNames[state]}`}
                title={isEditing ? 'Bấm để chỉnh sửa máy' : clickable ? (playing ? 'Bấm để chuyển về Trống' : 'Bấm để chuyển sang Đang chơi') : ''}
                onClick={() => isEditing ? editMachine(machine) : (clickable && togglePlaying(machine))}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                    e.preventDefault();
                    if (isEditing) editMachine(machine);
                    else if (clickable) togglePlaying(machine);
                  }
                }}>
                <span className={`status-dot ${state}`} />
                {isEditing && (
                  <span className="machine-card-actions visible" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="machine-card-btn edit" title={`Chỉnh sửa ${machine.name}`}
                      onClick={(e) => { e.stopPropagation(); editMachine(machine); }}>
                      <Pencil size={12} />
                    </button>
                    <button type="button" className="machine-card-btn delete" title={`Xóa ${machine.name}`}
                      onClick={(e) => { e.stopPropagation(); deleteMachine(machine); }}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                )}
                <span className="machine-type-icon"><MachineTypeIcon type={machine.resource_type} /></span>
                <strong>{machine.name}</strong>
                <em>{typeName[machine.resource_type] || machine.resource_type}</em>
                <small>{isEditing ? 'Bấm để sửa máy' : statusNames[state]}</small>
                {/* đối chiếu: máy đang bật bên Station */}
                {!isEditing && machine.station_session && <small className="machine-next station-ref">Station: {machine.station_session.paused ? 'tạm dừng' : 'đang chơi'} từ {timeOnly(machine.station_session.started_at)}</small>}
              </div>;
            })}
          </div>
          {rows.length > 0 && <div className="mini-bookings"><Table columns={miniColumns} rows={rows} /></div>}
        </section>;
      })}
    </>}

    {tab === 'list' && <>
      {can(auth, 'bookings.create') && <BookingInlineForm key={`${storeId}-${day}`} stores={stores} storeId={storeId} fail={fail} onSaved={() => { success('Đã thêm booking'); reloadAll(); }} />}
      {(upcoming.loading || upcoming.error || upcoming.rows?.length > 0) && <section className="card table-card">
        <Table columns={listColumns} rows={upcoming.rows} loading={upcoming.loading} error={upcoming.error} onRetry={upcoming.reload}
          actions={can(auth, 'bookings.update') ? (row) => <button className="table-action delete" onClick={() => hideBooking(row)} title="Xoá booking" aria-label={`Xoá booking của ${row.customer_name}`}><Trash2 size={15} /></button> : null} />
      </section>}
    </>}

    {tab === 'data' && canData && <CustomerData storeId={storeId} columns={[baseColumns[2], baseColumns[3], baseColumns[4], baseColumns[5], baseColumns[0], baseColumns[1], depositColumn, noteColumn, { key: 'status', label: 'Trạng thái', render: (v, row) => badge(row) }]} fail={fail} />}

    {config && <BookingConfig stores={stores} areas={areas} resources={resources} auth={auth} fail={fail} success={success} onClose={() => setConfig(false)} />}
    {reorderStore && (
      <MachineTypeReorderModal
        store={reorderStore}
        fail={fail}
        onClose={() => setReorderStore(null)}
        onSaved={() => {
          setReorderStore(null);
          success('Đã cập nhật thứ tự hiển thị loại máy');
          reloadAll();
        }}
      />
    )}
    {dialog}
  </main>;
}

/** Hộp thoại cho Chủ nhượng quyền / HQ tự sắp xếp thứ tự hiển thị loại máy & số máy cụ thể */
function MachineTypeReorderModal({ store, fail, onClose, onSaved }) {
  const allTypes = resourceTypes;
  const [types, setTypes] = useState(() => {
    const list = [...(store.current_order || [])];
    allTypes.forEach((t) => { if (!list.includes(t)) list.push(t); });
    return list;
  });

  // Gom máy theo loại máy
  const [machinesByType, setMachinesByType] = useState(() => {
    const map = {};
    allTypes.forEach((t) => { map[t] = []; });
    (store.machines || []).forEach((m) => {
      const t = map[m.resource_type] ? m.resource_type : 'Other';
      map[t].push(m);
    });
    return map;
  });

  const [activeTab, setActiveTab] = useState('types'); // 'types' | typeName
  const [saving, setSaving] = useState(false);

  const moveType = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= types.length) return;
    const copy = [...types];
    const [item] = copy.splice(index, 1);
    copy.splice(target, 0, item);
    setTypes(copy);
  };

  const moveMachine = (type, index, dir) => {
    const list = machinesByType[type] || [];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const copy = [...list];
    const [item] = copy.splice(index, 1);
    copy.splice(target, 0, item);
    setMachinesByType({ ...machinesByType, [type]: copy });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Gom lại resource_order theo thứ tự loại máy và thứ tự máy đã sắp xếp
      const resourceOrder = [];
      types.forEach((t) => {
        (machinesByType[t] || []).forEach((m) => resourceOrder.push(m.id));
      });
      await api.put(`${op}/stores/${store.store_id}/type-order`, {
        type_order: types,
        resource_order: resourceOrder,
      });
      onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const subTabs = [
    { id: 'types', label: '1. Thứ tự loại máy' },
    ...types.filter((t) => (machinesByType[t] || []).length > 0).map((t) => ({
      id: t,
      label: `2. Số máy ${typeName[t] || t} (${(machinesByType[t] || []).length})`,
    })),
  ];

  return (
    <Modal title={`Sắp xếp hiển thị máy - ${store.store_name}`} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {subTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`mm-chip ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 650,
              cursor: 'pointer',
              border: activeTab === t.id ? '1px solid var(--cyan)' : '1px solid var(--line)',
              background: activeTab === t.id ? 'rgba(39,185,250,.15)' : 'var(--surface-2)',
              color: activeTab === t.id ? '#fff' : 'var(--muted)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit}>
        {activeTab === 'types' ? (
          <div>
            <p className="modal-note" style={{ marginBottom: 14 }}>
              Di chuyển mũi tên để đổi thứ tự nhóm loại máy nào hiển thị trước/sau trên hàng máy trống.
            </p>
            <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
              {types.map((type, idx) => (
                <div
                  key={type}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 12,
                    background: 'var(--surface-2)',
                    border: '1px solid var(--line)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'var(--surface)',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 700,
                        fontSize: 13,
                        color: 'var(--cyan)',
                      }}
                    >
                      {idx + 1}
                    </span>
                    <div>
                      <strong>{typeName[type] || type}</strong>
                      <small style={{ display: 'block', color: 'var(--muted)', fontSize: 11.5 }}>
                        {(machinesByType[type] || []).length} máy
                      </small>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="table-action"
                      disabled={idx === 0 || saving}
                      onClick={() => moveType(idx, -1)}
                      title="Di chuyển lên trước"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      className="table-action"
                      disabled={idx === types.length - 1 || saving}
                      onClick={() => moveType(idx, 1)}
                      title="Di chuyển xuống sau"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <p className="modal-note" style={{ marginBottom: 14 }}>
              Sắp xếp thứ tự các máy thuộc loại <strong>{typeName[activeTab] || activeTab}</strong>. Máy ở trên sẽ đứng trước trên hàng máy.
            </p>
            <div style={{ display: 'grid', gap: 10, marginBottom: 20, maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
              {(machinesByType[activeTab] || []).map((machine, idx) => (
                <div
                  key={machine.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 12,
                    background: 'var(--surface-2)',
                    border: '1px solid var(--line)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'var(--surface)',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        color: 'var(--cyan)',
                      }}
                    >
                      {idx + 1}
                    </span>
                    <strong>{machine.name}</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="table-action"
                      disabled={idx === 0 || saving}
                      onClick={() => moveMachine(activeTab, idx, -1)}
                      title="Di chuyển lên trước"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      className="table-action"
                      disabled={idx === (machinesByType[activeTab] || []).length - 1 || saving}
                      onClick={() => moveMachine(activeTab, idx, 1)}
                      title="Di chuyển xuống sau"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions">
          <Button type="button" onClick={onClose} disabled={saving}>Hủy</Button>
          <Button type="submit" tone="primary" disabled={saving}>
            {saving ? <RefreshCw className="spin" size={16} /> : null} Lưu thứ tự
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Form thêm booking nằm sẵn trên đầu tab "Thông tin khách đặt": đặt theo loại máy, không chọn máy cụ thể. */
function BookingInlineForm({ stores, storeId, fail, onSaved }) {
  const [form, setForm] = useState(() => blankForm(storeId, today()));
  const [saving, setSaving] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const store = form.store_id || stores[0]?.id || '';

  const submit = async (event) => {
    event.preventDefault();
    if (form.phone.length !== 10) { setPhoneError(true); return; }
    setSaving(true);
    try {
      await api.post(`${op}/bookings`, {
        store_id: Number(store), resource_type: form.resource_type, customer_name: form.customer_name.trim() || 'Khách lẻ', phone: form.phone,
        starts_at: fromLocalInput(`${form.date || today()}T${form.time || '00:00'}`),
        party_size: Math.max(1, Number(form.party_size) || 1), deposit: Number(form.deposit || 0), notes: form.notes.trim() || null,
      });
      set({ customer_name: '', phone: '', deposit: '', notes: '' });
      onSaved();
    } catch (error) { fail(error); } finally { setSaving(false); }
  };

  return <form className="card booking-inline-form" onSubmit={submit}>
    <div className="booking-inline-row">
      <Field label="Tên khách" value={form.customer_name} onChange={(value) => set({ customer_name: value })} placeholder="Vd: Thiện" maxLength={160} />
      <Field label="Số điện thoại" value={form.phone} onChange={(value) => set({ phone: value.replace(/\D/g, '').slice(0, 10) })} inputMode="numeric" placeholder="10 chữ số" maxLength={10} required />
      <Field label="Ngày đặt" type="date" value={form.date} onChange={(value) => set({ date: value })} required />
      <Field label="Giờ đặt" type="time" value={form.time} onChange={(value) => set({ time: value })} required />
      <Select label="Cơ sở" value={store} onChange={(value) => set({ store_id: value })} options={stores} placeholder={null} required />
      <Select label="Loại máy" value={form.resource_type} onChange={(value) => set({ resource_type: value })} options={bookableTypes.map((type) => ({ id: type, name: typeName[type] || type }))} placeholder={null} required />
      <Field label="Số người" type="number" min="1" value={form.party_size} onChange={(value) => set({ party_size: value })} />
      <MoneyField label="Đặt cọc (VNĐ)" value={form.deposit} onChange={(value) => set({ deposit: value })} placeholder="VD: 50.000" />
    </div>
    <div className="booking-inline-actions">
      <Field label="Nhân viên nhận - Ghi chú" value={form.notes} onChange={(value) => set({ notes: value })} placeholder="Nhập tên nhân viên và ghi chú..." maxLength={1000} />
      <Button tone="primary" disabled={saving}>{saving ? <RefreshCw className="spin" size={16} /> : <Plus size={16} />} Thêm Booking</Button>
    </div>
    {phoneError && <Modal title="Sai số điện thoại" onClose={() => setPhoneError(false)}>
      <p className="modal-note">Số điện thoại phải đủ 10 chữ số.</p>
      <div className="form-actions"><Button type="button" tone="primary" onClick={() => setPhoneError(false)}>Okie, Biết rồi</Button></div>
    </Modal>}
  </form>;
}

/** Tab "Dữ liệu khách hàng": khách Đã đến (kể cả booking đã xoá khỏi màn hình) + tải Excel theo tuần / Zip lưu trữ. */
function CustomerData({ storeId, columns, fail }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const history = useQuery(`${op}/bookings${queryString({ store_id: storeId, history: 1, page })}`);
  const index = useQuery(open ? `${op}/bookings/export/weeks${queryString({ store_id: storeId })}` : null, { realtime: false });
  const download = async (key, path, filename) => {
    setBusy(key);
    try { await api.download(path, filename); } catch (error) { fail(error); } finally { setBusy(''); }
  };
  const weeks = index.data?.weeks || [];
  const archives = index.data?.archives || [];

  return <>
    <section className="card panel">
      <div className="panel-title"><h2>Quản lý xuất dữ liệu</h2><Button tone="primary" onClick={() => setOpen(!open)}><Download size={16} /> Tải dữ liệu</Button></div>
      {open && <div className="export-panel">
        {index.error && <p className="field-error">{index.error}</p>}
        {index.loading && !index.data && <p className="modal-note"><RefreshCw className="spin" size={15} /> Đang tải danh sách tuần...</p>}
        {index.data && <>
          <h3 className="sub-title">Dữ liệu 52 tuần (file Excel)</h3>
          <div className="export-grid">{weeks.map((week) => <button type="button" key={week.start} className="export-file" disabled={busy === week.start || week.count === 0}
            title={`${dayLabel(week.start)} – ${dayLabel(week.end)}`} onClick={() => download(week.start, `${op}/bookings/export/week${queryString({ store_id: storeId, start: week.start })}`, `Booking ${week.label}.xlsx`)}>
            {busy === week.start ? <RefreshCw className="spin" size={14} /> : <FileSpreadsheet size={14} />} <span>{week.label}</span><small>{week.count} booking</small>
          </button>)}</div>
          <h3 className="sub-title">Dữ liệu lưu trữ (file Zip) – tối đa 2 file</h3>
          {archives.length === 0 ? <p className="modal-note">Chưa có dữ liệu cũ hơn 52 tuần.</p>
            : <div className="export-grid">{archives.map((archive) => <button type="button" key={archive.part} className="export-file archive" disabled={busy === `zip${archive.part}`}
              title={`${dayLabel(archive.from)} – ${dayLabel(archive.to)}`} onClick={() => download(`zip${archive.part}`, `${op}/bookings/export/archive${queryString({ store_id: storeId, part: archive.part })}`, `Booking 52 tuần - Phần ${archive.part}.zip`)}>
              {busy === `zip${archive.part}` ? <RefreshCw className="spin" size={14} /> : <FileArchive size={14} />} <span>{archive.label}</span><small>{archive.count} booking</small>
            </button>)}</div>}
        </>}
      </div>}
    </section>
    <section className="card table-card">
      <div className="panel-title"><h2>Dữ liệu khách hàng hiện tại</h2></div>
      <Table columns={columns} rows={history.rows} loading={history.loading} error={history.error} onRetry={history.reload} empty="Chưa có khách nào được đánh dấu Đã đến" />
      <Pagination pagination={history.pagination} onPage={setPage} />
    </section>
  </>;
}

function BookingConfig({ stores, areas, resources, auth, fail, success, onClose }) {
  const manage = can(auth, 'branches.manage');
  const [area, setArea] = useState({ store_id: stores[0]?.id || '', name: '' });
  const [resource, setResource] = useState({ store_id: stores[0]?.id || '', area_id: '', name: '', resource_type: 'PS5' });
  const [ask, dialog] = useAsk();
  const reload = () => { areas.reload(); resources.reload(); };
  const run = async (work, message) => { try { await work(); success(message); reload(); } catch (error) { fail(error); } };

  const addArea = (event) => { event.preventDefault(); run(async () => { await api.post(`${op}/booking-areas`, { store_id: Number(area.store_id), name: area.name }); setArea({ ...area, name: '' }); }, 'Đã thêm khu vực'); };
  const addResource = (event) => { event.preventDefault(); run(async () => { await api.post(`${op}/booking-resources`, { ...resource, store_id: Number(resource.store_id), area_id: resource.area_id ? Number(resource.area_id) : null }); setResource({ ...resource, name: '' }); }, 'Đã thêm máy'); };
  const remove = async (path, name, row) => {
    if (!await ask({ title: `Xóa "${name}"?`, message: 'Chỉ xóa được mục chưa có lịch sử booking. Nếu máy đã từng có khách đặt, hệ thống sẽ đề xuất chuyển sang trạng thái Ngừng hoạt động.', danger: true, confirmLabel: 'Xóa' })) return;
    try {
      await api.delete(path);
      success('Đã xóa thành công');
      reload();
    } catch (err) {
      if (row && (err?.code === 'RESOURCE_IN_USE' || err?.message?.includes('RESOURCE_IN_USE') || err?.message?.includes('lịch sử booking'))) {
        const deactivate = await ask({
          title: 'Mục đã có lịch sử sử dụng',
          message: `"${name}" đã có dữ liệu trước đây nên không thể xóa hoàn toàn khỏi cơ sở dữ liệu. Bạn có muốn chuyển sang trạng thái "Ngừng hoạt động" để ẩn khỏi danh sách không?`,
          confirmLabel: 'Ngừng hoạt động',
        });
        if (deactivate) {
          try {
            await api.put(`${op}/booking-resources/${row.id}`, {
              name: row.name,
              resource_type: row.resource_type,
              status: 'inactive',
              area_id: row.area_id ? Number(row.area_id) : null,
            });
            success(`Đã chuyển "${name}" sang Ngừng hoạt động`);
            reload();
          } catch (deactivateErr) {
            fail(deactivateErr);
          }
        }
      } else {
        fail(err);
      }
    }
  };
  const editArea = async (row) => {
    const values = await ask({ title: 'Sửa khu vực', fields: [{ key: 'name', label: 'Tên khu vực', required: true, defaultValue: row.name }, { key: 'status', label: 'Trạng thái', required: true, defaultValue: row.status, placeholder: null, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'inactive', name: 'Ngừng' }] }] });
    if (values) run(() => api.put(`${op}/booking-areas/${row.id}`, values), 'Đã sửa khu vực');
  };
  const editResource = async (row) => {
    const storeAreas = (areas.rows || []).filter((item) => Number(item.store_id) === Number(row.store_id));
    const values = await ask({ title: 'Sửa máy', fields: [
      { key: 'name', label: 'Tên máy', required: true, defaultValue: row.name },
      { key: 'resource_type', label: 'Loại máy', required: true, defaultValue: row.resource_type, placeholder: null, options: resourceTypes.map((id) => ({ id, name: typeName[id] || id })) },
      { key: 'status', label: 'Trạng thái', required: true, defaultValue: row.status, placeholder: null, options: [{ id: 'active', name: 'Hoạt động' }, { id: 'maintenance', name: 'Bảo trì' }, { id: 'inactive', name: 'Ngừng hoạt động' }] },
      ...(storeAreas.length > 0 ? [{ key: 'area_id', label: 'Khu vực', defaultValue: row.area_id ? String(row.area_id) : '', placeholder: 'Không chia khu vực', options: [{ id: '', name: 'Không chia khu vực' }, ...storeAreas.map((a) => ({ id: String(a.id), name: a.name }))] }] : []),
    ] });
    if (values) run(() => api.put(`${op}/booking-resources/${row.id}`, { ...values, area_id: values.area_id ? Number(values.area_id) : null }), 'Đã sửa máy');
  };

  return <Modal title="Quản lý máy và khu vực booking" onClose={onClose} wide>
    {manage && <div className="ops-config-grid">
      <form onSubmit={addArea}><Select label="Cơ sở" value={area.store_id} onChange={(value) => setArea({ ...area, store_id: value })} options={stores} required /><Field label="Tên khu vực" value={area.name} onChange={(value) => setArea({ ...area, name: value })} required minLength={2} /><Button tone="primary"><Plus size={15} /> Thêm khu vực</Button></form>
      <form onSubmit={addResource}><Select label="Cơ sở" value={resource.store_id} onChange={(value) => setResource({ ...resource, store_id: value, area_id: '' })} options={stores} required />
        <Select label="Khu vực" value={resource.area_id} onChange={(value) => setResource({ ...resource, area_id: value })} options={(areas.rows || []).filter((item) => Number(item.store_id) === Number(resource.store_id))} placeholder="Không chia khu vực" />
        <Field label="Tên máy" value={resource.name} onChange={(value) => setResource({ ...resource, name: value })} required placeholder="VD: Máy 1" />
        <Select label="Loại máy" value={resource.resource_type} onChange={(value) => setResource({ ...resource, resource_type: value })} options={resourceTypes.map((id) => ({ id, name: typeName[id] || id }))} placeholder={null} required />
        <Button tone="primary"><Plus size={15} /> Thêm máy</Button></form>
    </div>}
    <h3>Danh sách máy</h3>
    <Table rows={resources.rows} loading={resources.loading} error={resources.error} onRetry={resources.reload} columns={[{ key: 'store_name', label: 'Cơ sở' }, { key: 'area_name', label: 'Khu vực' }, { key: 'name', label: 'Tên máy' }, { key: 'resource_type', label: 'Loại', render: (v) => typeName[v] || v }, { key: 'status', label: 'Trạng thái', render: (v) => <Status value={v} /> }]}
      actions={manage ? (row) => <><button className="table-action edit" onClick={() => editResource(row)} title="Sửa máy"><Pencil size={13} /> Sửa</button><button className="table-action delete" onClick={() => remove(`${op}/booking-resources/${row.id}`, row.name, row)} title="Xóa máy"><Trash2 size={13} /> Xóa</button></> : null} />
    <h3>Khu vực</h3>
    <Table rows={areas.rows} loading={areas.loading} error={areas.error} onRetry={areas.reload} empty="Chưa chia khu vực" columns={[{ key: 'store_name', label: 'Cơ sở' }, { key: 'name', label: 'Tên khu vực' }, { key: 'status', label: 'Trạng thái', render: (v) => <Status value={v} /> }]}
      actions={manage ? (row) => <><button className="table-action edit" onClick={() => editArea(row)} title="Sửa khu vực"><Pencil size={13} /> Sửa</button><button className="table-action delete" onClick={() => remove(`${op}/booking-areas/${row.id}`, row.name)} title="Xóa khu vực"><Trash2 size={13} /> Xóa</button></> : null} />
    {dialog}
  </Modal>;
}
